/**
 * frontend/src/App.js
 *
 * BUG FIX (this version): /loans route was wrapped in
 * W(ADMIN_HR, <LoanManagement/>) — ADMIN_HR does not include 'employee',
 * so PrivateRoute redirected every employee straight to /dashboard before
 * LoanManagement.js ever got a chance to render its own internal
 * employee-vs-HR view split. LoanManagement.js already handles that role
 * branching internally (see its isHR check), so the route itself only
 * needed to allow ALL authenticated roles through — same pattern already
 * used correctly for /attendance/regularize, /leave, /expenses, etc.
 * Changed to W(ALL, <LoanManagement/>).
 *
 * /probation is intentionally left as ADMIN_HR-only — that page has no
 * employee-facing view at all (it's purely an HR review dashboard), so
 * restricting it remains correct.
 *
 * All other routes and imports are otherwise identical to your deployed
 * version, including the CandidatePortal public route added earlier.
 */
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastContainer } from 'react-toastify';
import PrivateRoute from './components/PrivateRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import 'react-toastify/dist/ReactToastify.css';
import './assets/css/style.css';
// ── Auth / Public ─────────────────────────────────────────────────────────
import Login             from './views/auth/Login';
import TenantSignup      from './views/auth/TenantSignup';
import { ForgotPassword, ResetPassword } from './views/auth/PasswordReset';
import SignupIntents from './views/superadmin/SignupIntents';
import LoginLogs from './views/superadmin/LoginLogs';
// ── ATS Public (no auth) ─────────────────────────────────────────────────
import CareersPage       from './views/ats/CareersPage';
import CandidatePortal   from './views/ats/CandidatePortal';
// ── EmployeeVerify  ─────────────────────────────────────────────────
import EmployeeVerify from './views/employees/EmployeeVerify';
// ── Core ──────────────────────────────────────────────────────────────────
import DashboardHome     from './views/dashboard/DashboardHome';
import Profile           from './views/profile/Profile';
import ESSHub            from './views/ess/ESSHub';
import EmployeeList      from './views/employees/EmployeeList';
// ── HR Core ───────────────────────────────────────────────────────────────
import DeptDesigPage       from './views/hr/DeptDesigPage';
import LocationsPage       from './views/hr/LocationsPage';
import OrgChart            from './views/hr/OrgChart';
import OnboardingChecklist from './views/hr/OnboardingChecklist';
import BrandingSettings    from './views/setting/BrandingSettings';
import TimesheetPage       from './views/hr/TimesheetPage';
import ShiftManagement     from './views/hr/ShiftManagement';
import ShiftRoster from './views/shifts/ShiftRoster';
// ── Workforce ─────────────────────────────────────────────────────────────
import AttendanceCalendar  from './views/attendance/AttendanceCalendar';
import LeaveRequest        from './views/leaves/LeaveRequest';
import DocumentCenter      from './views/documents/DocumentCenter';
import ManageAnnouncements from './views/announcements/ManageAnnouncements';
import HolidayCalendar     from './views/hr/HolidayCalendar';
// ── Payroll ───────────────────────────────────────────────────────────────
import PayrollManagement      from './views/payroll/PayrollManagement';
import PayrollArrears         from './views/payroll/PayrollArrears';
import PayrollFinanceApproval from './views/payroll/PayrollFinanceApproval';
import InvestmentDeclaration  from './views/payroll/InvestmentDeclaration';
import MyPayslips             from './views/payroll/MyPayslips';
import TaxSummaryReport       from './views/admin/TaxSummaryReport';
import DeductionForecast      from './components/admin/DeductionForecast';
import LoanManagement from './views/payroll/LoanManagement';
import ProbationTracking from './views/employees/ProbationTracking';
// ── ATS ───────────────────────────────────────────────────────────────────
import ATSPage             from './views/ats/ATSPage';
import MyInterviewsPage    from './views/ats/MyInterviewsPage';
// ── Performance ───────────────────────────────────────────────────────────
import PerformancePage     from './views/performance/PerformancePage';
import TrainingPage        from './views/performance/TrainingPage';
import SkillMatrixPage     from './views/performance/SkillMatrixPage';
// ── Other modules ─────────────────────────────────────────────────────────
import AIAssistant       from './views/ai/AIAssistant';
import RemoteWorkPage    from './views/remotework/RemoteWorkPage';
import WorkflowPage      from './views/workflows/WorkflowPage';
import SurveysPage       from './views/surveys/SurveysPage';
import PulseSurveyPage   from './views/surveys/PulseSurveyPage';
import ExpensesPage      from './views/expenses/ExpensesPage';
import HelpdeskPage      from './views/helpdesk/HelpdeskPage';
import ReportsPage       from './views/reports/ReportsPage';
import OnboardingPage    from './views/onboarding/OnboardingPage';
// ── Offboarding ───────────────────────────────────────────────────────────
import OffboardingPage   from './views/offboarding/OffboardingPage';
import ExitInterview     from './views/offboarding/ExitInterview';
import ExitInterviewList from './views/offboarding/ExitInterviewList';
// ── Sprint 7 ──────────────────────────────────────────────────────────────
import AttendanceRegularization from './views/attendance/AttendanceRegularization';
import AuditLogViewer           from './views/hr/AuditLogViewer';
// ── Compliance ────────────────────────────────────────────────────────────
import ComplianceCalendar from './views/compliance/ComplianceCalendar';
// ── Super Admin ───────────────────────────────────────────────────────────
import BillingPage      from './views/billing/BillingPage';
import InquiriesPage    from './views/superadmin/InquiriesPage';
import SuperDashboard   from './views/superadmin/SuperDashboard';
import ManageCompanies  from './views/superadmin/ManageCompanies';
import ManagePlans      from './views/superadmin/ManagePlans';
import PlatformSettings from './views/superadmin/PlatformSettings';
import PromoCodesPage   from './views/superadmin/PromoCodesPage';
import SystemLogs       from './views/superadmin/SystemLogs';
// ── Role groups ───────────────────────────────────────────────────────────
const ALL       = ['super-admin','admin','hr-manager','hiring-manager','recruiter','employee','account-manager','reporting-manager'];
const ADMIN_HR  = ['super-admin','admin','hr-manager','account-manager'];
const ATS_ROLES = ['super-admin','admin','hr-manager','hiring-manager','recruiter'];
const ADMIN     = ['super-admin','admin','account-manager'];
const SA        = ['super-admin'];
const W = (roles, children) => (
    <PrivateRoute allowedRoles={roles}>
        <DashboardLayout>{children}</DashboardLayout>
    </PrivateRoute>
);
const ComingSoon = ({ title = 'Coming Soon' }) => (
    <div className="p-5 text-center bg-white rounded shadow-sm mt-4">
        <h4 className="text-primary fw-bold">{title}</h4>
        <p className="text-muted">This module is being finalised.</p>
    </div>
);
function AppContent() {
    const { user } = useAuth();
    useEffect(() => {
        const c = user?.primary_color || '#4f46e5';
        document.documentElement.style.setProperty('--primary-color', c);
    }, [user?.primary_color]);
    return (
        <Router>
            <Routes>
                {/* ── Root → Login ──────────────────────────────────────── */}
                <Route path="/" element={<Navigate to="/login" replace />} />
                {/* ── Auth pages ────────────────────────────────────────── */}
                <Route path="/login"                 element={<Login />} />
                <Route path="/register"              element={<TenantSignup />} />
                <Route path="/forgot-password"       element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                {/* ── ATS Public (no auth) ──────────────────────────────── */}
                <Route path="/careers/:slug"         element={<CareersPage />} />
                <Route path="/candidate-portal"      element={<CandidatePortal />} />
                {/* ── EmployeeVerify ──────────────────────────────── */}
                <Route path="/verify/:uuid" element={<EmployeeVerify />} />
                {/* ── All authenticated roles ────────────────────────────── */}
                <Route path="/dashboard"             element={W(ALL, <DashboardHome />)} />
                <Route path="/profile"               element={W(ALL, <Profile />)} />
                <Route path="/my-profile"            element={W(ALL, <ESSHub />)} />
                <Route path="/attendance"            element={W(ALL, <AttendanceCalendar />)} />
                <Route path="/holidays"              element={W(ALL, <HolidayCalendar />)} />
                <Route path="/announcements"         element={W(ALL, <ManageAnnouncements />)} />
                <Route path="/leave"                 element={W(ALL, <LeaveRequest />)} />
                <Route path="/documents"             element={W(ALL, <DocumentCenter />)} />
                <Route path="/onboarding"            element={W(ALL, <OnboardingChecklist />)} />
                <Route path="/timesheet"             element={W(ALL, <TimesheetPage />)} />
                <Route path="/performance"           element={W(ALL, <PerformancePage />)} />
                <Route path="/training"              element={W(ALL, <TrainingPage />)} />
                <Route path="/skills"                element={W(ALL, <SkillMatrixPage />)} />
                <Route path="/expenses"              element={W(ALL, <ExpensesPage />)} />
                <Route path="/surveys"               element={W(ALL, <PulseSurveyPage />)} />
                <Route path="/helpdesk"              element={W(ALL, <HelpdeskPage />)} />
                <Route path="/my-team"               element={W(ALL, <EmployeeList myTeamOnly={true} />)} />
                <Route path="/org-chart"             element={W(ALL, <OrgChart />)} />
                <Route path="/offboarding"           element={W(ALL, <OffboardingPage />)} />
                <Route path="/exit-interview"        element={W(ALL, <ExitInterview />)} />
                <Route path="/ai-assistant"          element={W(ALL, <AIAssistant />)} />
                <Route path="/remote-work"           element={W(ALL, <RemoteWorkPage />)} />
                <Route path="/attendance/regularize" element={W(ALL, <AttendanceRegularization />)} />
                {/* ── Payroll — all roles ───────────────────────────────── */}
                <Route path="/payroll/my-payslips"            element={W(ALL, <MyPayslips />)} />
                <Route path="/payroll/investment-declaration" element={W(ALL, <InvestmentDeclaration />)} />
                {/* BUG FIX: was W(ADMIN_HR, ...) — LoanManagement.js already
                    branches internally on role (employee request view vs
                    HR approval view), so the route itself must allow ALL
                    roles through, same as /attendance/regularize above. */}
                <Route path="/loans"                 element={W(ALL, <LoanManagement/>)} />
                {/* ── ATS ───────────────────────────────────────────────── */}
                <Route path="/ats"                   element={W(ATS_ROLES, <ATSPage />)} />
                <Route path="/my-interviews"         element={W(ALL, <MyInterviewsPage />)} />
                <Route path="/compliance-calendar"   element={W(ADMIN_HR, <ComplianceCalendar />)} />
                {/* ── Admin + HR ────────────────────────────────────────── */}
                <Route path="/employees"             element={W(ADMIN_HR, <EmployeeList />)} />
                <Route path="/departments"           element={W(ADMIN_HR, <DeptDesigPage />)} />
                <Route path="/shifts"                element={W(ADMIN_HR, <ShiftManagement />)} />
                <Route path="/shifts/roster" element={W(ADMIN_HR, <ShiftRoster />)} />
                <Route path="/reports"               element={W(ADMIN_HR, <ReportsPage />)} />
                <Route path="/workflows"             element={W(ADMIN_HR, <WorkflowPage />)} />
                <Route path="/audit-logs"            element={W(ADMIN_HR, <AuditLogViewer />)} />
                <Route path="/offboarding/exit-interviews" element={W(ADMIN_HR, <ExitInterviewList />)} />
                <Route path="/payroll/management"    element={W(ADMIN_HR, <PayrollManagement />)} />
                <Route path="/payroll/arrears"       element={W(ADMIN_HR, <PayrollArrears />)} />
                <Route path="/payroll/finance-approval" element={W(ADMIN_HR, <PayrollFinanceApproval />)} />
                <Route path="/payroll/tax-summary"   element={W(ADMIN_HR, <><TaxSummaryReport /><DeductionForecast /></>)} />
                <Route path="/probation" element={W(ADMIN_HR, <ProbationTracking/>)} />
                {/* ── Admin only ────────────────────────────────────────── */}
                <Route path="/locations"             element={W(ADMIN, <LocationsPage />)} />
                <Route path="/billing"               element={W(ADMIN, <BillingPage />)} />
                <Route path="/settings/branding"     element={W(['admin'], <BrandingSettings />)} />
                {/* ── Super Admin ───────────────────────────────────────── */}
                <Route path="/superadmin/dashboard"  element={W(SA, <SuperDashboard />)} />
                <Route path="/superadmin/companies"  element={W(SA, <ManageCompanies />)} />
                <Route path="/superadmin/plans"      element={W(SA, <ManagePlans />)} />
                <Route path="/superadmin/settings"   element={W(SA, <PlatformSettings />)} />
                <Route path="/superadmin/promo-codes"element={W(SA, <PromoCodesPage />)} />
                <Route path="/superadmin/logs"       element={W(SA, <SystemLogs />)} />
                <Route path="/superadmin/inquiries"  element={W(SA, <InquiriesPage />)} />
                <Route path="/superadmin/signup-intents" element={W(SA, <SignupIntents />)} />
                <Route path="/superadmin/login-logs" element={W(SA, <LoginLogs />)} />
                {/* Catch-all */}
                <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
            </Routes>
            <ToastContainer position="top-right" autoClose={5000} newestOnTop closeOnClick pauseOnHover />
        </Router>
    );
}
export default function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}
